import {
  buildInventoryAdjustmentStockCommandV1,
  type StockCommandV1,
} from '@trackeat/shared';

export const INVENTORY_ATTESTED_REASON = 'inventory_attested' as const;
export const INVENTORY_ATTESTED_REF_PREFIX = 'inventory-attested:v1:' as const;

export interface BuildInventoryAttestedLotInputV1 {
  commandId: string;
  tenantId: string;
  businessId: string;
  foodId: string;
  batchLot: string;
  expirationDate: string | null;
  stockUom: string;
  countedSourceQty: number;
  countedSourceUom: string;
  conversionFactor: number;
  inventoryDate: string;
  inventoryReference: string;
  note: string;
  countedAt: string;
  operatorName: string;
}

function cleanText(value: string, field: string, max: number): string {
  const clean = value.trim().replaceAll('|', ' ');
  if (!clean || clean.length > max) {
    throw new Error(`inventory_attested_invalid_${field}`);
  }
  return clean;
}

function positive(value: number, field: string): number {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`inventory_attested_invalid_${field}`);
  }
  return value;
}

function formatNumber(value: number): string {
  return Number(value.toFixed(9)).toString();
}

function inventoryDate(value: string): string {
  const clean = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(clean)) {
    throw new Error('inventory_attested_invalid_inventory_date');
  }
  const parsed = new Date(`${clean}T12:00:00.000Z`);
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== clean) {
    throw new Error('inventory_attested_invalid_inventory_date');
  }
  return clean;
}

/**
 * The generic stock command remains the single writer. The reference carries
 * the supplied physical inventory evidence verbatim enough for an audit,
 * while `reason=inventory_attested` is reserved server-side to the platform
 * owner and deliberately has no commercial document, supplier or valuation.
 */
export function buildInventoryAttestedLotCommandV1(
  input: BuildInventoryAttestedLotInputV1,
): StockCommandV1 {
  const sourceQty = positive(input.countedSourceQty, 'counted_source_qty');
  const factor = positive(input.conversionFactor, 'conversion_factor');
  const countedQty = sourceQty * factor;
  if (!Number.isFinite(countedQty) || countedQty <= 0) {
    throw new Error('inventory_attested_invalid_counted_stock_qty');
  }

  const batchLot = cleanText(input.batchLot, 'batch_lot', 100);
  const stockUom = cleanText(input.stockUom, 'stock_uom', 10).toLowerCase();
  const sourceUom = cleanText(input.countedSourceUom, 'counted_source_uom', 10)
    .toLowerCase();
  const countedOn = inventoryDate(input.inventoryDate);
  const inventoryReference = cleanText(input.inventoryReference, 'reference', 80);
  const note = input.note.trim().replaceAll('|', ' ');
  if (note.length > 60) throw new Error('inventory_attested_invalid_note');
  const lineRef = [
    `${INVENTORY_ATTESTED_REF_PREFIX}${input.commandId}`,
    `data=${countedOn}`,
    `rif=${inventoryReference}`,
    `conteggio=${formatNumber(sourceQty)} ${sourceUom}`,
    `fattore=${formatNumber(factor)}`,
    `saldo=${formatNumber(countedQty)} ${stockUom}`,
    ...(note ? [`nota=${note}`] : []),
  ].join('|');
  if (lineRef.length > 200) throw new Error('inventory_attested_reference_too_long');

  return buildInventoryAdjustmentStockCommandV1({
    commandId: input.commandId,
    tenantId: input.tenantId,
    businessId: input.businessId,
    foodId: input.foodId,
    lot: batchLot,
    expirationDate: input.expirationDate,
    systemQty: 0,
    countedQty,
    uom: stockUom,
    countedAt: input.countedAt,
    reason: INVENTORY_ATTESTED_REASON,
    operatorName: input.operatorName,
    unitCost: null,
    currency: 'EUR',
    provenance: {
      supplierId: null,
      documentId: null,
      storageUnitId: null,
      lineRef,
    },
  });
}
